"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { getBrowserSupabaseClient } from "@/lib/supabase/browser-client";
import type { Database } from "@/types/supabase";

type SupabaseProviderProps = {
  children: ReactNode;
};

type SupabaseContextValue = SupabaseClient<Database>;

const SupabaseContext = createContext<SupabaseContextValue | null>(null);

export function SupabaseProvider({ children }: SupabaseProviderProps) {
  // IMPORTANT: Use getBrowserSupabaseClient() to ensure singleton instance
  // This prevents multiple Supabase clients which causes token refresh storms
  const supabase = useMemo(() => {
    try {
      return getBrowserSupabaseClient();
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[SupabaseProvider] Skipping client initialization. Ensure NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY are defined.",
          error,
        );
      }
      return null;
    }
  }, []);

  if (!supabase) {
    return <>{children}</>;
  }

  return (
    <SupabaseContext.Provider value={supabase}>
      {children}
    </SupabaseContext.Provider>
  );
}

export function useSupabase() {
  const context = useContext(SupabaseContext);

  if (!context) {
    throw new Error("useSupabase must be used within a SupabaseProvider.");
  }

  return context;
}

/**
 * The client when the provider could initialise one, otherwise null.
 *
 * For code that renders on public pages and only decorates with sign-in
 * state. The provider renders its children without a context when the
 * Supabase env is missing (preview deployments, placeholder CI builds), and
 * a marketing page that prerenders must not fail its build over that: the
 * /pricing export did on 2026-10-07 the moment the page stopped suspending.
 */
export function useOptionalSupabase() {
  return useContext(SupabaseContext);
}
